import type { ComponentProps} from 'react';
import { useContext, useEffect, useMemo } from 'react';
import isEqual from 'lodash.isequal';

import type { BaseField, FieldDescriptor, StaticFieldProps } from '../../types/common';
import type { ContestManageModalType } from '../../../components/Modals/ContestManageModalsContext';
import { ContestManageModalsContext } from '../../../components/Modals/ContestManageModalsContext';
import type { ModalActionType } from '../../../components/Modals/types/GenericModal/GenericModal';
import type RewardsProgramModal from '../../../components/Modals/types/RewardsProgramModal/RewardsProgramModal';


export type ModalType = BaseField & {
  type: 'Modal';
  actions?: ModalActionType[];
} & (
  | {
    modalType: '',
  }
  | {
    modalType: 'SelfManagedInfoModal';
  }
  | {
    modalType: 'CollectEntryFeesInfoModal';
  }
  | {
    modalType: 'RewardsProgramModal';
    payload?: Omit<ComponentProps<typeof RewardsProgramModal>, 'isOpen' | 'onClose'>;
  }
);

function ModalElement({ field, isVisible }: StaticFieldProps<ModalType>) {
  const { openModal, modals } = useContext(ContestManageModalsContext);

  const payload = useMemo(() => {
    switch (field.modalType) {
      case 'SelfManagedInfoModal':
        return {
          type: 'SelfManageInfoModal',
        } as ContestManageModalType;
      case 'CollectEntryFeesInfoModal':
        return {
          type: 'CollectEntryFeesInfoModal',
        } as ContestManageModalType;
      case 'RewardsProgramModal':
        return {
          type: 'RewardsProgramModal',
          ...field.payload,
        } as ContestManageModalType;
      default:
        return {
          type: 'GenericModal',
          title: field.label,
          description: field.description,
          actions: field.actions,
        } as ContestManageModalType;
    }
  }, [field]);

  useEffect(() => {
    const isRendered = modals.find((modal) => (
      isEqual(modal, payload)
    ));
    if (isRendered) { return; }
    if (!isVisible) { return; }

    openModal(payload);
  }, [isVisible, payload]);


  return null;
}

const Descriptor: FieldDescriptor<ModalType> = {
  FieldComponent: ModalElement,
  name: 'Modal',
  renderIfHidden: true,
  rendersNull: true,
};

export default Descriptor;
